import { createClient } from 'contentful'
import Head from 'next/head'
import { useRouter } from 'next/router'
import styled, { keyframes } from 'styled-components'

import useIsMobile from '@/util/useIsMobile'
import { urlParser } from '@/util/imgHelpers'

export async function getServerSideProps() {
  const spaceId = process.env.CONTENTFUL_SPACE_ID
  const accessToken = process.env.CONTENTFUL_ACCESS_KEY

  if (!spaceId || !accessToken) {
    throw '.env.local variables are not setup correctly.'
  }

  const client = createClient({
    space: spaceId,
    accessToken: accessToken,
    environment: process.env.CONTENTFUL_ENV
  })

  const aboutPageEntryPromise = client.getEntries({
    content_type: 'aboutPage',
    limit: 1
  })

  const aboutPageEntry = await aboutPageEntryPromise
  if (aboutPageEntry.items.length === 0) return { notFound: true }

  return {
    props: {
      aboutPageEntry: aboutPageEntry.items[0]
    }
  }
}

const CHARITY_PARTNERS = [
  {
    name: 'One Tree Planted',
    url: 'https://onetreeplanted.org/',
    description: 'Reforestation efforts worldwide'
  },
  {
    name: 'Waves For Water',
    url: 'https://www.wavesforwater.org/',
    description: 'Clean water access in the Philippines'
  },
  {
    name: 'Gospel Ministries International',
    url: 'https://gospelministry.org/',
    description: 'Building schools for children'
  },
  {
    name: 'LA Food Bank',
    url: 'https://lafoodbank.org/',
    description: 'Fighting hunger in Los Angeles'
  }
]

const IMPACT_STATS = [
  { number: '$100,000+', label: 'Donated', icon: '💚' },
  { number: '22,000+', label: 'Trees Planted', icon: '🌱' },
  { number: '1', label: 'School Built', icon: '🏫' },
  { number: '4', label: 'Charity Partners', icon: '🤝' }
]

export default function About({ aboutPageEntry }: { aboutPageEntry: Record<string, any> }) {
  const isMobile = useIsMobile()
  const router = useRouter()

  if (isMobile === undefined) return null

  const authorImgSrc = urlParser(aboutPageEntry.fields.photoOfAuthor?.fields?.file?.url)
  const aboutMainBody: string[] = aboutPageEntry.fields.aboutMainBody
    .split('\n')
    .filter((p: string) => p.trim())

  return (
    <>
      <Head>
        <title>About — Beyond The Pines</title>
        <meta
          name="description"
          content="Learn about Beyond The Pines Magazine and our mission to create beautiful art while making the world a better place."
        />
      </Head>

      <PageWrapper>
        {/* About Section with Video Background */}
        <AboutSection>
          <AboutGrid>
            <AboutImageWrapper>
              <AboutImage src={authorImgSrc} alt="BadBoi - Creator of Beyond The Pines" />
            </AboutImageWrapper>
            <AboutContent>
              <HeroLine>In pixels we see time and life.</HeroLine>

              <AboutText>Passion sees the photo before it&apos;s captured.</AboutText>
              <AboutText>Inspired vision navigates the lens with familiar fascination.</AboutText>
              <AboutTextAccent>Inhalation&apos;s whisper of creativity.</AboutTextAccent>
              <AboutText>While fingers orchestrate an exhaled symphony of raw files.</AboutText>

              <AboutTextMuted>
                The value of a picture is not proportionate to the trouble and expense it costs to
                obtain it, but to the poetry that it contains.
              </AboutTextMuted>

              <AboutText>
                Welcome to our digital experience: <Highlight>Beyond the Pines</Highlight>, by
                Badboi. It&apos;s best enjoyed by someone just like you.
              </AboutText>

              <InstagramLink
                href="https://instagram.com/badboi"
                target="_blank"
                rel="noopener noreferrer"
              >
                @badboi
              </InstagramLink>
            </AboutContent>
          </AboutGrid>
        </AboutSection>
        {/* Mission Section */}
        <MissionSection>
          <MissionVideo autoPlay muted loop playsInline>
            <source
              src="https://videos.ctfassets.net/rxdl0mk5gigv/4WqAJO8qTHrzpI5LzQel7t/2e8ede1acd027fdeea8f9e453f3d8a74/about-video.mp4"
              type="video/mp4"
            />
          </MissionVideo>
          <MissionOverlay />
          <MissionContent>
            <SectionLabel>Our Mission</SectionLabel>
            <MissionTitle>Art With Purpose</MissionTitle>
            <MissionText>
              The mission of Beyond The Pines is not only to create beautiful art, but also to help
              make this a better world. We donate <Highlight>30% of proceeds</Highlight> from all
              subscriptions, merch, and magazine sales to charities that plant trees, build schools,
              and provide food and clean drinking water for families in need.
            </MissionText>
          </MissionContent>
        </MissionSection>

        {/* Impact Stats */}
        <ImpactSection>
          <ImpactHeader>
            <SectionLabel>Our Impact</SectionLabel>
            <ImpactTitle>Together We&apos;re Making a Difference</ImpactTitle>
          </ImpactHeader>

          <StatsGrid>
            {IMPACT_STATS.map((stat, index) => (
              <StatCard key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                <StatIcon>{stat.icon}</StatIcon>
                <StatNumber>{stat.number}</StatNumber>
                <StatLabel>{stat.label}</StatLabel>
              </StatCard>
            ))}
          </StatsGrid>
        </ImpactSection>

        {/* Charity Partners */}
        <PartnersSection>
          <PartnersHeader>
            <SectionLabel>Charity Partners</SectionLabel>
            <PartnersTitle>Organizations We Support</PartnersTitle>
          </PartnersHeader>

          <PartnersGrid>
            {CHARITY_PARTNERS.map((partner, index) => (
              <PartnerCard
                key={index}
                href={partner.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <PartnerName>{partner.name}</PartnerName>
                <PartnerDescription>{partner.description}</PartnerDescription>
                <PartnerLink>Visit Website →</PartnerLink>
              </PartnerCard>
            ))}
          </PartnersGrid>
        </PartnersSection>

        {/* Photo Gallery */}
        <GallerySection>
          <GalleryHeader>
            <SectionLabel>In Action</SectionLabel>
            <GalleryTitle>Where Your Support Goes</GalleryTitle>
          </GalleryHeader>

          <GalleryCategory>
            <GalleryCategoryTitle>Schools Built</GalleryCategoryTitle>
            <GalleryGrid>
              <GalleryImage src="/images/school-1.jpg" alt="School building project" />
              <GalleryImage src="/images/school-2.jpg" alt="School building project" />
              <GalleryImage src="/images/school-3.jpg" alt="School building project" />
            </GalleryGrid>
            <GalleryCaption>
              Funded by our donation to Gospel Ministries International
            </GalleryCaption>
          </GalleryCategory>

          <GalleryCategory>
            <GalleryCategoryTitle>Clean Water Projects</GalleryCategoryTitle>
            <GalleryGrid>
              <GalleryImage src="/images/water-1.jpg" alt="Clean water project" />
              <GalleryImage src="/images/water-2.jpg" alt="Clean water project" />
              <GalleryImage src="/images/water-3.jpg" alt="Clean water project" />
            </GalleryGrid>
            <GalleryCaption>Funded by our donation to Waves For Water Philippines</GalleryCaption>
          </GalleryCategory>

          <GalleryCategory>
            <GalleryCategoryTitle>Trees Planted</GalleryCategoryTitle>
            <GalleryGrid>
              <GalleryImage src="/images/tree-1.jpg" alt="Tree planting project" />
              <GalleryImage src="/images/tree-2.jpg" alt="Tree planting project" />
              <GalleryImage src="/images/tree-3.jpg" alt="Tree planting project" />
            </GalleryGrid>
            <GalleryCaption>Funded by our donation to One Tree Planted</GalleryCaption>
          </GalleryCategory>
        </GallerySection>

        {/* CTA Section */}
        <CTASection>
          <CTAContent>
            <CTATitle>Join The Movement</CTATitle>
            <CTAText>
              Every subscription helps us create more art and give back more to the world.
            </CTAText>
            <CTAButton onClick={() => router.push('/subscribe')}>Become a Member</CTAButton>
          </CTAContent>
        </CTASection>
      </PageWrapper>
    </>
  )
}

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`

// Styled Components
const PageWrapper = styled.div`
  min-height: 100vh;
  background: #0a0a0a;
`

const SectionLabel = styled.span`
  display: inline-block;
  font-family: 'Soehne-Kraftig', sans-serif;
  font-size: 11px;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  color: #6b8e23;
  margin-bottom: 16px;
`

// About Section
const AboutSection = styled.section`
  padding: 40px 20px 50px;
  background: #0a0a0a;

  @media (min-width: 800px) {
    padding: 60px 48px 100px;
  }
`

const AboutGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 32px;
  max-width: 1100px;
  margin: 0 auto;
  align-items: center;

  @media (min-width: 800px) {
    grid-template-columns: 1fr 1.2fr;
    gap: 64px;
  }
`

const AboutImageWrapper = styled.div`
  animation: ${fadeIn} 0.8s ease-out;
`

const AboutImage = styled.img`
  width: 100%;
  border-radius: 12px;
  aspect-ratio: 4/5;
  object-fit: cover;
`

const AboutContent = styled.div`
  animation: ${fadeIn} 0.8s ease-out 0.2s both;
`

const HeroLine = styled.h2`
  font-family: 'Soehne-Kraftig', sans-serif;
  font-size: clamp(22px, 4vw, 34px);
  color: white;
  margin: 0 0 24px;
  letter-spacing: -0.02em;
  line-height: 1.3;
`

const AboutText = styled.p`
  font-family: 'Soehne-Leicht', sans-serif;
  font-size: 15px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.7;
  margin: 0 0 14px;

  @media (min-width: 800px) {
    font-size: 16px;
  }
`

const AboutTextAccent = styled.p`
  font-family: 'Soehne-Leicht', sans-serif;
  font-size: 15px;
  color: #6B8E23;
  line-height: 1.7;
  margin: 0 0 14px;
  font-style: italic;

  @media (min-width: 800px) {
    font-size: 16px;
  }
`

const AboutTextMuted = styled.p`
  font-family: 'Soehne-Leicht', sans-serif;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.4);
  line-height: 1.7;
  margin: 20px 0;
  font-style: italic;

  @media (min-width: 800px) {
    font-size: 15px;
  }
`

const Highlight = styled.span`
  color: #6b8e23;
  font-family: 'Soehne-Kraftig', sans-serif;
`

const InstagramLink = styled.a`
  display: inline-block;
  font-family: 'Soehne-Kraftig', sans-serif;
  font-size: 13px;
  color: #6b8e23;
  text-decoration: none;
  margin-top: 6px;
  transition: color 0.2s ease;

  &:hover {
    color: #8fbc3a;
  }
`

// Mission Section
const MissionSection = styled.section`
  position: relative;
  padding: 80px 24px;
  background: #0a0a0a;
  overflow: hidden;

  @media (min-width: 800px) {
    padding: 120px 48px;
  }
`

const MissionVideo = styled.video`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  min-width: 100%;
  min-height: 100%;
  width: auto;
  height: auto;
  object-fit: cover;
  z-index: 0;
`

const MissionOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(10, 10, 10, 0.75) 0%,
    rgba(10, 10, 10, 0.7) 50%,
    rgba(10, 10, 10, 0.8) 100%
  );
  z-index: 1;
`

const MissionContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
  animation: ${fadeIn} 0.6s ease-out;
`

const MissionTitle = styled.h2`
  font-family: 'Soehne-Kraftig', sans-serif;
  font-size: clamp(28px, 5vw, 44px);
  color: white;
  margin: 0 0 24px;
  letter-spacing: -0.02em;
`

const MissionText = styled.p`
  font-family: 'Soehne-Leicht', sans-serif;
  font-size: 18px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.8;
  margin: 0;
`

// Impact Section
const ImpactSection = styled.section`
  padding: 80px 24px;
  background: #0a0a0a;

  @media (min-width: 800px) {
    padding: 100px 48px;
  }
`

const ImpactHeader = styled.div`
  text-align: center;
  margin-bottom: 48px;
`

const ImpactTitle = styled.h2`
  font-family: 'Soehne-Kraftig', sans-serif;
  font-size: clamp(24px, 4vw, 36px);
  color: white;
  margin: 0;
  letter-spacing: -0.01em;
`

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  max-width: 900px;
  margin: 0 auto;

  @media (min-width: 700px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 24px;
  }
`

const StatCard = styled.div`
  text-align: center;
  padding: 32px 20px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  animation: ${fadeIn} 0.5s ease-out both;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(107, 142, 35, 0.3);
    transform: translateY(-4px);
  }
`

const StatIcon = styled.span`
  font-size: 32px;
  display: block;
  margin-bottom: 12px;
`

const StatNumber = styled.div`
  font-family: 'Soehne-Kraftig', sans-serif;
  font-size: clamp(24px, 4vw, 36px);
  color: #6b8e23;
  margin-bottom: 4px;
`

const StatLabel = styled.div`
  font-family: 'Soehne-Leicht', sans-serif;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 1px;
`

// Partners Section
const PartnersSection = styled.section`
  padding: 80px 24px;
  background: #111;

  @media (min-width: 800px) {
    padding: 100px 48px;
  }
`

const PartnersHeader = styled.div`
  text-align: center;
  margin-bottom: 48px;
`

const PartnersTitle = styled.h2`
  font-family: 'Soehne-Kraftig', sans-serif;
  font-size: clamp(24px, 4vw, 36px);
  color: white;
  margin: 0;
`

const PartnersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 16px;
  max-width: 1000px;
  margin: 0 auto;

  @media (min-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 900px) {
    grid-template-columns: repeat(4, 1fr);
  }
`

const PartnerCard = styled.a`
  display: block;
  padding: 28px 24px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  text-decoration: none;
  animation: ${fadeIn} 0.5s ease-out both;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(107, 142, 35, 0.1);
    border-color: rgba(107, 142, 35, 0.3);
    transform: translateY(-4px);
  }
`

const PartnerName = styled.h3`
  font-family: 'Soehne-Kraftig', sans-serif;
  font-size: 16px;
  color: white;
  margin: 0 0 8px;
`

const PartnerDescription = styled.p`
  font-family: 'Soehne-Leicht', sans-serif;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
  margin: 0 0 16px;
  line-height: 1.5;
`

const PartnerLink = styled.span`
  font-family: 'Soehne-Kraftig', sans-serif;
  font-size: 12px;
  color: #6b8e23;
  letter-spacing: 0.5px;
`

// Gallery Section
const GallerySection = styled.section`
  padding: 80px 24px;
  background: #0a0a0a;

  @media (min-width: 800px) {
    padding: 100px 48px;
  }
`

const GalleryHeader = styled.div`
  text-align: center;
  margin-bottom: 48px;
`

const GalleryTitle = styled.h2`
  font-family: 'Soehne-Kraftig', sans-serif;
  font-size: clamp(24px, 4vw, 36px);
  color: white;
  margin: 0;
`

const GalleryCategory = styled.div`
  max-width: 1100px;
  margin: 0 auto 48px;

  &:last-of-type {
    margin-bottom: 0;
  }
`

const GalleryCategoryTitle = styled.h3`
  font-family: 'Soehne-Kraftig', sans-serif;
  font-size: 18px;
  color: white;
  margin: 0 0 16px;
  text-align: center;
`

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;

  @media (min-width: 800px) {
    gap: 16px;
  }
`

const GalleryImage = styled.img`
  width: 100%;
  aspect-ratio: 4/3;
  object-fit: cover;
  border-radius: 8px;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.02);
  }
`

const GalleryCaption = styled.p`
  font-family: 'Soehne-Leicht', sans-serif;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.4);
  text-align: center;
  margin: 12px 0 0;
`

// CTA Section
const CTASection = styled.section`
  padding: 100px 24px;
  background: linear-gradient(180deg, #111 0%, #0a0a0a 100%);
  border-top: 1px solid rgba(255, 255, 255, 0.06);

  @media (min-width: 800px) {
    padding: 120px 48px;
  }
`

const CTAContent = styled.div`
  text-align: center;
  max-width: 500px;
  margin: 0 auto;
`

const CTATitle = styled.h2`
  font-family: 'Soehne-Kraftig', sans-serif;
  font-size: clamp(28px, 5vw, 42px);
  color: white;
  margin: 0 0 16px;
  letter-spacing: -0.02em;
`

const CTAText = styled.p`
  font-family: 'Soehne-Leicht', sans-serif;
  font-size: 16px;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.6;
  margin: 0 0 32px;
`

const CTAButton = styled.button`
  background: linear-gradient(135deg, #556b2f 0%, #6b8e23 100%);
  border: none;
  border-radius: 8px;
  color: white;
  font-family: 'Soehne-Kraftig', sans-serif;
  font-size: 14px;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  padding: 16px 36px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(85, 107, 47, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`
